import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon, ClockIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { AsyncStatus } from '../../../../@types/AsyncStatus';
import type { SCIListItem } from '../../../../@types/SCI';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import { ROUTES } from '../../../../utils/routes';
import Badge from '../../../common/Badge';
import MUITable from '../../../common/MUITable';
import { showToast } from '../../../common/Notification';
import RedirectionMapButtons from '../../../common/RedirectionMapButtons/RedirectionMapButtons.module';
import Spinner from '../../../common/Spinner';
import DismissibleModuleTutorialPopup from '../../../common/Tutorials/DismissibleModuleTutorialPopup';
import type { SCIContactTargets } from '../components/SCIContactQualificationModal';
import SCIContactQualificationModal from '../components/SCIContactQualificationModal';
import type { SCIQualifiedContact } from '../components/SCIQualifiedContactsTable';
import {
  buildSciExportContent,
  describeSciExportFilters,
  downloadSciExportWorkbook,
  encodeExportContent,
  getSciDisplayName,
  sciExportFileName,
} from './sciExport';
import SCIExportHistory from './SCIExportHistory';
import SCIExportModal from './SCIExportModal';
import type { SciListState } from './sciListQuery';
import {
  buildSciApiParams,
  countActiveFilters,
  DEFAULT_SCI_LIST_STATE,
  parseLotsRange,
  parseSciListState,
  readStoredSciListView,
  serializeSciListState,
  storeSciListView,
} from './sciListQuery';

type LotsDraft = Pick<SciListState, 'lotsCoproMin' | 'lotsCoproMax' | 'lotsTotalMin' | 'lotsTotalMax'>;

const LOTS_DEBOUNCE_MS = 400;

const lotsDraftOf = (state: SciListState): LotsDraft => ({
  lotsCoproMin: state.lotsCoproMin,
  lotsCoproMax: state.lotsCoproMax,
  lotsTotalMin: state.lotsTotalMin,
  lotsTotalMax: state.lotsTotalMax,
});

const sameLots = (a: LotsDraft, b: LotsDraft) => (
  a.lotsCoproMin === b.lotsCoproMin && a.lotsCoproMax === b.lotsCoproMax
  && a.lotsTotalMin === b.lotsTotalMin && a.lotsTotalMax === b.lotsTotalMax
);

const yesNoBadge = (value?: boolean | null) => (
  <span className={`rounded px-2 py-1 text-xs ${value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
    {value ? 'Oui' : 'Non'}
  </span>
);

// "Client en gestion" has three states: never answered is shown apart from "Non"
const managedBadge = (value?: boolean | null) => {
  if (value === true) {
    return <span className="rounded bg-green-100 px-2 py-1 text-xs text-green-700">Oui</span>;
  }
  if (value === false) {
    return <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">Non</span>;
  }

  return <span className="px-2 py-1 text-xs text-gray-400" title="Non renseigné">—</span>;
};

interface LotsRangeInputProps {
  id: string;
  label: string;
  min: string;
  max: string;
  error: string | null;
  onChange: (bound: 'min' | 'max', value: string) => void;
}

const LotsRangeInput: React.FC<LotsRangeInputProps> = ({ id, label, min, max, error, onChange }) => (
  <div className="flex flex-col" role="group" aria-labelledby={`${id}-label`}>
    <span id={`${id}-label`} className="text-xs text-gray-600">{label}</span>
    <div className="flex items-center gap-1">
      <input
        aria-label={`${label} : minimum`}
        aria-invalid={!!error}
        className={`w-14 rounded border px-2 py-1 sm:w-16 ${error ? 'border-red-500' : ''}`}
        inputMode="numeric"
        maxLength={6}
        placeholder="de"
        value={min}
        onChange={(event) => onChange('min', event.target.value)}
      />
      <span className="text-xs text-gray-500">à</span>
      <input
        aria-label={`${label} : maximum`}
        aria-invalid={!!error}
        className={`w-14 rounded border px-2 py-1 sm:w-16 ${error ? 'border-red-500' : ''}`}
        inputMode="numeric"
        maxLength={6}
        placeholder="à"
        value={max}
        onChange={(event) => onChange('max', event.target.value)}
      />
    </div>
    {error && <p role="alert" className="mt-0.5 max-w-40 text-xs text-red-600">{error}</p>}
  </div>
);

const SCIsOverall: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rows, setRows] = useState<SCIListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [activeSiren, setActiveSiren] = useState<string | null>(null);
  const [activeSciName, setActiveSciName] = useState<string>('');
  const [isContactOpen, setContactOpen] = useState(false);
  const [activeContact, setActiveContact] = useState<SCIQualifiedContact | null>(null);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setExportModalOpen] = useState(false);
  const [isExportHistoryOpen, setExportHistoryOpen] = useState(false);
  const [exportHistoryKey, setExportHistoryKey] = useState(0);
  const [kpis, setKpis] = useState<{ totalScis: number; totalLotsWithin: number; totalLotsTotal: number } | null>(null);

  // Filters, search, sort and page live in the URL: back from a SCI or copro page, a refresh or a shared link keep the view
  const queryKey = searchParams.toString();
  const query = useMemo(() => parseSciListState(new URLSearchParams(queryKey)), [queryKey]);
  const filtersKey = useMemo(() => buildSciApiParams(query, { pagination: false }).toString(), [query]);
  const activeFiltersCount = countActiveFilters(query);

  const [searchTerm, setSearchTerm] = useState(query.search);
  const [lotsDraft, setLotsDraft] = useState<LotsDraft>(() => lotsDraftOf(query));
  const lotsCoproRange = parseLotsRange(lotsDraft.lotsCoproMin, lotsDraft.lotsCoproMax);
  const lotsTotalRange = parseLotsRange(lotsDraft.lotsTotalMin, lotsDraft.lotsTotalMax);

  // Every URL write starts from the current URL. setSearchParams(fn) would hand back the params of the
  // render that created the callback (react-router 6), so a delayed write (lot debounce) could undo a newer change.
  const ownQueryRef = useRef<string | null>(null);
  // Only the latest request may update the table: a slow response must not overwrite a newer filter
  const rowsRequestRef = useRef(0);
  const kpisRequestRef = useRef(0);
  const writeQuery = useCallback((next: URLSearchParams) => {
    ownQueryRef.current = next.toString();
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  const updateQuery = useCallback((patch: Partial<SciListState>, resetPage = true) => {
    writeQuery(serializeSciListState({
      ...parseSciListState(new URLSearchParams(window.location.search)),
      ...patch,
      ...(resetPage ? { page: 0 } : {}),
    }));
  }, [writeQuery]);

  // The view in the URL belongs to one company. Opened with an empty URL, the page shows this tab's last view;
  // a company switch in the header (no remount) swaps to the new company's last view.
  const [viewCompanyId, setViewCompanyId] = useState<number | null>(null);
  const isViewReady = !!company?.id && viewCompanyId === company.id;
  useEffect(() => {
    if (!company?.id || viewCompanyId === company.id) {return;}
    const stored = readStoredSciListView(company.id);
    if (viewCompanyId !== null) {
      // Nothing of the previous company may stay on screen, nor land from a request still in flight
      rowsRequestRef.current += 1;
      kpisRequestRef.current += 1;
      setRows([]);
      setTotalCount(0);
      setKpis(null);
      setHasLoaded(false);
      writeQuery(new URLSearchParams(stored ?? ''));
    } else if (!queryKey && stored) {
      writeQuery(new URLSearchParams(stored));
    }
    setViewCompanyId(company.id);
  }, [company?.id]);

  // An empty URL we did not write ourselves (the menu entry of this page, clicked while on it) shows the last view too
  const isClearedFromOutside = !queryKey && ownQueryRef.current !== '';
  useEffect(() => {
    if (!isViewReady || !isClearedFromOutside || !company?.id) {return;}
    const stored = readStoredSciListView(company.id);
    if (stored) {
      writeQuery(new URLSearchParams(stored));
    }
  }, [isViewReady, queryKey]);

  useEffect(() => {
    if (company?.id && isViewReady && !isClearedFromOutside) {
      storeSciListView(company.id, queryKey);
    }
  }, [company?.id, isViewReady, queryKey]);

  // Keep the inputs in line with the URL (reset, restored view, back/forward)
  useEffect(() => {
    setSearchTerm(query.search);
  }, [query.search]);
  useEffect(() => {
    setLotsDraft((draft) => (sameLots(draft, lotsDraftOf(query)) ? draft : lotsDraftOf(query)));
  }, [query.lotsCoproMin, query.lotsCoproMax, query.lotsTotalMin, query.lotsTotalMax]);

  // Lot bounds apply shortly after typing, only once both ranges are valid. Any URL change re-arms the timer,
  // so it never fires on an outdated view.
  useEffect(() => {
    if (sameLots(lotsDraft, lotsDraftOf(query)) || lotsCoproRange.error || lotsTotalRange.error) {return undefined;}
    const timer = window.setTimeout(() => updateQuery(lotsDraft), LOTS_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [lotsDraft, queryKey]);

  useEffect(() => {
    if (!company?.id || !jwt || !isViewReady) {return;}
    const requestId = ++rowsRequestRef.current;
    setStatus('PENDING');
    (async() => {
      try {
        const resp = await fetch(`/api/companies/${company.id}/scis?${buildSciApiParams(query, { pagination: true }).toString()}`, JWTHeader(jwt));
        if (!resp.ok) {throw new Error(`${resp.status}`);}
        const data = await resp.json();
        if (requestId !== rowsRequestRef.current) {return;}
        const results: SCIListItem[] = data.results || [];
        const pagination = data.pagination || { totalResults: results.length, currentPage: query.page + 1 };
        setRows(results);
        setTotalCount(pagination.totalResults || results.length);
        setStatus('SUCCESS');
        setHasLoaded(true);
        // The API caps the page to the last one (e.g. a stored page 5 on a narrower filter)
        const servedPage = Math.max(0, (pagination.currentPage ?? (query.page + 1)) - 1);
        if (servedPage !== query.page) {
          updateQuery({ page: servedPage }, false);
        }
      } catch (e) {
        if (requestId !== rowsRequestRef.current) {return;}
        console.error(e);
        setErrorMessage('Erreur lors du chargement des SCIs');
        setStatus('ERROR');
        showToast('error', 'Une erreur est survenue lors du chargement des données.');
      }
    })();
  }, [company?.id, jwt, isViewReady, queryKey]);

  useEffect(() => {
    if (!company?.id || !jwt || !isViewReady) {return;}
    const requestId = ++kpisRequestRef.current;
    (async() => {
      try {
        const r = await fetch(`/api/companies/${company.id}/scis/kpis?${filtersKey}`, JWTHeader(jwt));
        if (!r.ok || requestId !== kpisRequestRef.current) { return; }
        const data = await r.json();
        if (requestId !== kpisRequestRef.current) { return; }
        setKpis({
          totalScis: Number(data.totalScis ?? 0),
          totalLotsWithin: Number(data.totalLotsWithin ?? 0),
          totalLotsTotal: Number(data.totalLotsTotal ?? 0),
        });
      } catch {
        // silent
      }
    })();
  }, [company?.id, jwt, isViewReady, filtersKey]);

  const handlePageChange = (nextPage: number, nextRowsPerPage: number) => {
    updateQuery({ page: nextPage, rowsPerPage: nextRowsPerPage }, false);
  };

  const handleHeaderSort = (field: string) => {
    const nextDir: 'asc'|'desc' = (query.sortField === field && query.sortDirection === 'asc') ? 'desc' : 'asc';
    updateQuery({ sortField: field, sortDirection: nextDir });
  };

  const handleLotsChange = (range: 'copro' | 'total', bound: 'min' | 'max', value: string) => {
    const key = `lots${range === 'copro' ? 'Copro' : 'Total'}${bound === 'min' ? 'Min' : 'Max'}` as keyof LotsDraft;
    setLotsDraft((draft) => ({ ...draft, [key]: value.trim() }));
  };

  // Typed but not applied yet: unsubmitted search, lot bounds still waiting or invalid
  const hasPendingInput = searchTerm.trim() !== query.search || !sameLots(lotsDraft, lotsDraftOf(query));

  const handleResetFilters = () => {
    // Sort and page size are kept: only what narrows the list is cleared, typed values included
    const { search, qualified, targeted, targetedTransaction, alreadyManaged, lotsCoproMin, lotsCoproMax, lotsTotalMin, lotsTotalMax } = DEFAULT_SCI_LIST_STATE;
    setSearchTerm(search);
    setLotsDraft(lotsDraftOf(DEFAULT_SCI_LIST_STATE));
    updateQuery({ search, qualified, targeted, targetedTransaction, alreadyManaged, lotsCoproMin, lotsCoproMax, lotsTotalMin, lotsTotalMax });
  };

  const handleSearchSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }
    const trimmed = searchTerm.trim();
    setSearchTerm(trimmed);
    // Enter also applies the lot bounds right away when they are valid
    const lots = lotsCoproRange.error || lotsTotalRange.error ? {} : lotsDraft;
    updateQuery({ search: trimmed, ...lots });
  };

  const fetchQualification = async(siren: string) => {
    if (!company?.id || !jwt) {return null;}
    const r = await fetch(`/api/companies/${company.id}/scis/${siren}/qualification/contact`, JWTHeader(jwt));
    if (!r.ok) {return null;}
    const q = await r.json();
    const contact: SCIQualifiedContact | null = q && (q.civilite || q.nom || q.prenom || q.email || q.telephone || q.adresse) ? q : null;
    return contact;
  };

  // Resolves false when nothing was saved, so that the modal stays open with what was typed
  const handleContactSave = async(siren: string, contact: SCIQualifiedContact, targets?: SCIContactTargets): Promise<boolean> => {
    setIsSavingContact(true);
    try {
      const r = await fetch(`/api/companies/${company.id}/scis/${siren}/qualification/contact`, {
        method: 'PUT',
        headers: { Authorization: JWTAuthorization(jwt), 'Content-Type': 'application/json' },
        body: JSON.stringify(contact),
      });
      if (!r.ok) { throw new Error(`${r.status}`); }
    } catch {
      showToast('error', 'Enregistrement du contact impossible.');
      setIsSavingContact(false);
      return false;
    }

    // Same rule as the API: a contact is qualified once it has a name and an email
    const contactQualified = !!contact.nom?.trim() && !!contact.email?.trim();
    const updateRow = (patch: Partial<NonNullable<SCIListItem['qualification']>>) => setRows((prev) => prev.map((it) => (it.siren === siren
      ? { ...it, qualification: { ...(it.qualification || {}), ...patch } }
      : it)));
    updateRow({ contactQualified, contact });
    setActiveContact(contact);

    let targetsSaved = true;
    if (targets) {
      try {
        const t = await fetch(`/api/companies/${company.id}/scis/${siren}/qualification`, {
          method: 'PATCH',
          headers: { Authorization: JWTAuthorization(jwt), 'Content-Type': 'application/json' },
          body: JSON.stringify(targets),
        });
        if (!t.ok) { throw new Error(`${t.status}`); }
        updateRow(targets);
      } catch {
        targetsSaved = false;
      }
    }
    setIsSavingContact(false);

    if (!targetsSaved) {
      // The contact is saved: keep the modal open so that the targets can be saved again
      showToast('warning', "Contact enregistré, mais les cibles n'ont pas pu l'être. Réessayez.");
      return false;
    }
    showToast('success', 'Contact enregistré avec succès.');

    return true;
  };

  const handleContactDelete = async(siren: string) => {
    try {
      setIsSavingContact(true);
      await fetch(`/api/companies/${company.id}/scis/${siren}/qualification/contact`, {
        method: 'DELETE',
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      setRows((prev) => prev.map((it) => (it.siren === siren
        ? { ...it, qualification: { ...(it.qualification || {}), contactQualified: false, contact: null } }
        : it)));
      setActiveContact(null);
      showToast('success', 'Contact supprimé.');
    } catch {
      showToast('error', 'Suppression du contact impossible.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const openContactModal = async(siren: string, sciName: string) => {
    setActiveSiren(siren);
    setActiveSciName(sciName);
    setActiveContact(null);
    setContactOpen(true);
    setIsLoadingContact(true);
    try {
      const q = await fetchQualification(siren);
      setActiveContact(q);
    } finally {
      setIsLoadingContact(false);
    }
  };

  const exportToExcel = async(campaign: boolean, campaignName: string | null) => {
    if (!company?.id || !jwt) {return;}
    if (isExporting) {return;}
    setIsExporting(true);
    try {
      // Fetch ALL rows across pagination with current filters/sorting
      const params = buildSciApiParams(query, { pagination: true });
      params.set('limit', '1000');
      params.set('step', '1');

      const firstResp = await fetch(`/api/companies/${company.id}/scis?${params.toString()}`, JWTHeader(jwt));
      if (!firstResp.ok) { throw new Error(`${firstResp.status}`); }
      const firstData = await firstResp.json();
      let allRows: SCIListItem[] = firstData.results || [];
      const pagination = firstData.pagination || { totalResults: allRows.length, itemsPerPage: allRows.length, currentPage: 1, totalPages: 1 };
      const totalPages = pagination.totalPages || Math.ceil((pagination.totalResults || allRows.length) / (pagination.itemsPerPage || 1000));

      if (totalPages > 1) {
        const fetches: Promise<Response>[] = [];
        for (let step = 2; step <= totalPages; step += 1) {
          const p = new URLSearchParams(params);
          p.set('step', String(step));
          fetches.push(fetch(`/api/companies/${company.id}/scis?${p.toString()}`, JWTHeader(jwt)));
        }
        const responses = await Promise.all(fetches);
        // A missing page would silently truncate the file kept in the history
        if (responses.some((r) => !r.ok)) { throw new Error('Incomplete SCI export'); }
        const jsons = await Promise.all(responses.map(async(r) => r.json()));
        jsons.forEach((d) => { if (Array.isArray(d.results)) { allRows = [...allRows, ...(d.results as SCIListItem[])]; } });
      }

      if (allRows.length === 0) {
        showToast('warning', 'Aucune donnée à exporter.');
        return;
      }

      const content = buildSciExportContent(allRows);
      await downloadSciExportWorkbook(content, sciExportFileName(campaignName, new Date()));
      setExportModalOpen(false);

      // The file is already downloaded: a failed save only costs the history entry
      try {
        const body = new FormData();
        body.append('campaign', campaign ? '1' : '0');
        if (campaignName) { body.append('name', campaignName); }
        body.append('filters', JSON.stringify(Object.fromEntries(serializeSciListState(query))));
        const encoded = await encodeExportContent(content);
        body.append('content', encoded.blob, encoded.filename);
        const resp = await fetch(`/api/companies/${company.id}/sci-exports`, { method: 'POST', headers: { Authorization: JWTAuthorization(jwt) }, body });
        if (!resp.ok) { throw new Error(`${resp.status}`); }
        setExportHistoryKey((key) => key + 1);
      } catch (e) {
        console.error(e);
        showToast('warning', "Le fichier a été téléchargé mais l'export n'a pas pu être ajouté à l'historique.");
      }
    } catch (e) {
      console.error(e);
      showToast('error', "Une erreur est survenue lors de l'export.");
    } finally {
      setIsExporting(false);
    }
  };

  const applyExportFilters = (filters: Record<string, string>) => {
    writeQuery(serializeSciListState({
      ...parseSciListState(new URLSearchParams(filters)),
      page: 0,
      rowsPerPage: query.rowsPerPage,
    }));
    setExportHistoryOpen(false);
  };

  const activeRow = activeSiren ? rows.find((it) => it.siren === activeSiren) : undefined;
  const activeTargets = useMemo(() => ({
    targetedGestion: activeRow?.qualification?.targetedGestion ?? null,
    targetedTransaction: activeRow?.qualification?.targetedTransaction ?? null,
  }), [activeRow?.qualification?.targetedGestion, activeRow?.qualification?.targetedTransaction]);

  const columnsData = rows.map((sci) => {
    const primaryProp = sci.properties?.find((p) => p && p.copro);
    const copro = sci.primaryCopro || primaryProp?.copro;

    const seatAddress = `${sci.numeroVoieEtablissement || ''} ${sci.typeVoieEtablissement || ''} ${sci.libelleVoieEtablissement || ''}, ${sci.codePostalEtablissement || ''} ${sci.libelleCommuneEtablissement || ''}`;

    return ({
    'Nom immeuble': (
      <div className="flex flex-col items-start">
        <div className="flex items-center">
            <RedirectionMapButtons address={`${copro?.address || seatAddress}`} />
            <span className="ml-1 font-semibold">{copro?.name || 'N/A'}</span>
          </div>
          {copro?.id && (
            <button
              className="mt-1 ml-7 flex items-center text-xs text-rgpd-blue hover:underline"
              onClick={() => navigate(ROUTES.ANALYZING_COPRO_MANAGING_ID.replace(':coproId', copro.id!.toString()))}
            >
              <ArrowTopRightOnSquareIcon className="mr-1 h-3 w-3" /> Voir la fiche copro
            </button>
          )}
      </div>
    ),
    'Adresse immeuble': (
      <div className="text-xs text-gray-600">
        {copro?.address && (
          <>
            {copro.address}
            {copro.cp ? `, ${copro.cp}` : ''}
            {copro.city ? ` ${copro.city}` : ''}
          </>
        )}
      </div>
    ),
    'Nom SCI': (
      <span className="font-semibold">{getSciDisplayName(sci)}</span>
    ),
    'Adresse du siège de la SCI': (
      <div className="text-xs text-gray-600">
        {seatAddress.trim()}
      </div>
    ),
    'Lots dans la copro': sci.stats.propertiesWithinCopro,
    'Total lots SCI': sci.stats.totalProperties,
    'Contact qualifié': yesNoBadge(sci.qualification?.contactQualified),
    'Cible Gest Loc': yesNoBadge(sci.qualification?.targetedGestion),
    'Cible Transac': yesNoBadge(sci.qualification?.targetedTransaction),
    'Client en gestion': managedBadge(sci.qualification?.alreadyManaged),
    Actions: (
      <div className="flex flex-col space-y-2">
        <button
          className="rounded border border-orange-500 px-2 py-1 text-orange-600 hover:bg-orange-500 hover:text-white"
          onClick={() => openContactModal(sci.siren, sci.name || sci.denominationUsuelleEtablissement || `SCI ${sci.siren}`)}
        >
          Qualifier le contact de la SCI
        </button>
        <button
          type="button"
          onClick={() => navigate(ROUTES.MONITORING_SCIS_SIREN.replace(':siren', sci.siren.toString()))}
          className="rounded border border-rgpd-blue-dark px-2 py-1 text-center text-rgpd-blue-dark hover:bg-rgpd-blue-dark hover:text-white"
        >
          Qualifier SCI cible
        </button>
      </div>
    ),
    });
  });

  const headersForTable = [
    { title: 'Nom immeuble' },
    { title: 'Adresse immeuble' },
    { title: 'Nom SCI', sortable: true, sortField: 'denominationUniteLegale', onSort: handleHeaderSort },
    { title: 'Adresse du siège de la SCI' },
    { title: 'Lots dans la copro', sortable: true, sortField: 'stats.propertiesWithinCopro', onSort: handleHeaderSort },
    { title: 'Total lots SCI', sortable: true, sortField: 'stats.totalProperties', onSort: handleHeaderSort },
    { title: 'Contact qualifié', sortable: true, sortField: 'qualification.contactQualified', onSort: handleHeaderSort },
    { title: 'Cible Gest Loc', sortable: true, sortField: 'qualification.targetedGestion', onSort: handleHeaderSort },
    { title: 'Cible Transac', sortable: true, sortField: 'qualification.targetedTransaction', onSort: handleHeaderSort },
    { title: 'Client en gestion', sortable: true, sortField: 'qualification.alreadyManaged', onSort: handleHeaderSort },
    { title: 'Actions' },
  ];

  return (
    <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
      <DismissibleModuleTutorialPopup tutorial="monitoring-scis-overall" />
      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded border bg-white p-3">
          <div className="text-center text-2xl font-bold">{kpis ? kpis.totalScis.toLocaleString() : '-'}</div>
          <div className="text-center text-xs text-gray-500 uppercase">SCI présentes dans vos copropriétés</div>
        </div>
        <div className="rounded border bg-white p-3">
          <div className="text-center text-2xl font-bold">{kpis ? kpis.totalLotsWithin.toLocaleString() : '-'}</div>
          <div className="text-center text-xs text-gray-500 uppercase">Nombre de lots gérés dans vos copropriétés</div>
        </div>
        <div className="rounded border bg-white p-3">
          <div className="text-center text-2xl font-bold">{kpis ? kpis.totalLotsTotal.toLocaleString() : '-'}</div>
          <div className="text-center text-xs text-gray-500 uppercase">Nombre total de lots gérés par ces SCI</div>
        </div>
      </div>

      {/* Filters and actions */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <form className="grid w-full grid-cols-2 items-start gap-2 text-sm sm:flex sm:w-auto sm:flex-wrap" onSubmit={handleSearchSubmit}>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600" htmlFor="sci-overall-qualified">Statut contact qualifié</label>
            <select
              id="sci-overall-qualified"
              className="w-full rounded border px-2 py-1 sm:w-auto"
              value={query.qualified}
              onChange={(e) => updateQuery({ qualified: e.target.value as SciListState['qualified'] })}
            >
              <option value="all">Tous</option>
              <option value="yes">Qualifiés</option>
              <option value="no">Non qualifiés</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600" htmlFor="sci-overall-targeted">Statut ciblé Gest Loc</label>
            <select
              id="sci-overall-targeted"
              className="w-full rounded border px-2 py-1 sm:w-auto"
              value={query.targeted}
              onChange={(e) => updateQuery({ targeted: e.target.value as SciListState['targeted'] })}
            >
              <option value="all">Tous</option>
              <option value="yes">Ciblés</option>
              <option value="no">Non ciblés</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600" htmlFor="sci-overall-transaction">Statut ciblé Transac</label>
            <select
              id="sci-overall-transaction"
              className="w-full rounded border px-2 py-1 sm:w-auto"
              value={query.targetedTransaction}
              onChange={(e) => updateQuery({ targetedTransaction: e.target.value as SciListState['targetedTransaction'] })}
            >
              <option value="all">Tous</option>
              <option value="yes">Ciblés</option>
              <option value="no">Non ciblés</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600" htmlFor="sci-overall-managed">Client en gestion</label>
            <select
              id="sci-overall-managed"
              className="w-full rounded border px-2 py-1 sm:w-auto"
              value={query.alreadyManaged}
              onChange={(e) => updateQuery({ alreadyManaged: e.target.value as SciListState['alreadyManaged'] })}
            >
              <option value="all">Tous</option>
              <option value="yes">Clients en gestion</option>
              <option value="no">Non (ou non renseigné)</option>
              <option value="unknown">Non renseigné</option>
            </select>
          </div>
          <LotsRangeInput
            id="sci-overall-lots-copro"
            label="Lots dans copro"
            min={lotsDraft.lotsCoproMin}
            max={lotsDraft.lotsCoproMax}
            error={lotsCoproRange.error}
            onChange={(bound, value) => handleLotsChange('copro', bound, value)}
          />
          <LotsRangeInput
            id="sci-overall-lots-total"
            label="Total lots SCI"
            min={lotsDraft.lotsTotalMin}
            max={lotsDraft.lotsTotalMax}
            error={lotsTotalRange.error}
            onChange={(bound, value) => handleLotsChange('total', bound, value)}
          />
          <div className="col-span-2 flex flex-col">
            <label className="text-xs text-gray-600" htmlFor="sci-overall-search">Recherche</label>
            <div className="flex items-center gap-2">
              <input
                id="sci-overall-search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="min-w-0 flex-1 rounded border px-2 py-1 sm:min-w-[260px]"
                placeholder="Nom/Adresse immeuble ou SCI"
                type="search"
              />
              <button
                type="submit"
                className="rounded bg-rgpd-blue px-3 py-2 text-white hover:bg-rgpd-blue-dark"
              >
                Rechercher
              </button>
            </div>
          </div>
        </form>
        <div className="flex items-center gap-2 self-end">
          <button
            onClick={handleResetFilters}
            disabled={activeFiltersCount === 0 && !hasPendingInput}
            className="flex items-center rounded border border-gray-400 px-3 py-2 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {activeFiltersCount > 0 ? `Réinitialiser (${activeFiltersCount})` : 'Réinitialiser'}
          </button>
          <button
            onClick={() => setExportHistoryOpen(true)}
            className="flex items-center rounded border border-gray-400 px-3 py-2 text-gray-700 hover:bg-gray-100"
          >
            <ClockIcon className="mr-2 h-5 w-5" /> Historique des exports
          </button>
          <button
            onClick={() => setExportModalOpen(true)}
            disabled={isExporting}
            aria-busy={isExporting}
            className="flex items-center rounded border border-rgpd-green px-3 py-2 text-rgpd-green hover:bg-rgpd-green hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="mr-2 h-5 w-5" /> Export
          </button>
        </div>
      </div>
      {status === 'PENDING' && !hasLoaded && (
        <p className="mx-10 my-8 text-center">
          <Spinner aria-label="Chargement" size="lg" />
        </p>
      )}
      {status === 'ERROR' && (
        <div className="my-10 text-center">
          <Badge mode="error" text={errorMessage} />
        </div>
      )}
      {status !== 'ERROR' && hasLoaded && (
        // The table stays in place while a new page or filter loads, so the view does not jump
        <div className={`relative transition-opacity ${status === 'PENDING' ? 'opacity-60' : ''}`} aria-busy={status === 'PENDING'}>
          {status === 'PENDING' && (
            <div className="absolute top-4 left-1/2 z-10 -translate-x-1/2">
              <Spinner aria-label="Chargement" size="md" />
            </div>
          )}
          <MUITable
            data={columnsData}
            headers={headersForTable}
            tableClass="min-w-full bg-white border text-sm"
            isSearchable={false}
            disablePagination={false}
            apiPagination={true}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            currentPage={query.page}
            currentRowsPerPage={query.rowsPerPage}
            currentSortField={query.sortField}
            currentSortDirection={query.sortDirection}
          />
          {status === 'SUCCESS' && rows.length === 0 && (
            <div className="my-6 text-center text-sm text-gray-600">
              Aucune SCI ne correspond à ces filtres.
              {activeFiltersCount > 0 && (
                <button type="button" onClick={handleResetFilters} className="ml-2 text-rgpd-blue underline">
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <SCIExportModal
        isOpen={isExportModalOpen}
        onClose={() => setExportModalOpen(false)}
        filterLabels={describeSciExportFilters(Object.fromEntries(serializeSciListState(query)))}
        totalCount={totalCount}
        isExporting={isExporting}
        onConfirm={exportToExcel}
      />
      <SCIExportHistory
        isOpen={isExportHistoryOpen}
        onClose={() => setExportHistoryOpen(false)}
        refreshKey={exportHistoryKey}
        onApplyFilters={applyExportFilters}
      />

      {/* Contact Modal */}
      <SCIContactQualificationModal
        isOpen={isContactOpen}
        onClose={() => setContactOpen(false)}
        siren={activeSiren ?? undefined}
        sciName={activeSciName}
        initialContact={activeContact}
        initialTargets={activeTargets}
        isSaving={isSavingContact}
        isLoading={isLoadingContact}
        onSave={async(contact, targets) => (activeSiren ? handleContactSave(activeSiren, contact, targets) : false)}
        onDelete={activeSiren ? async() => handleContactDelete(activeSiren) : undefined}
      />
    </div>
  );
};

export default SCIsOverall;
